import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import type { BrandColorCandidate, ClientBranding, Settings } from '@shared/types'
import { formatDashboardTitle } from '@tn/shared'
import { deriveBrandingVariants, extractLogoPalette } from '../../utils/branding'
import LogoUpload from '../../components/LogoUpload'
import BrandLookupButton from '../../components/BrandLookupButton'
import BrandPreviewHeader from '../../components/BrandPreviewHeader'
import FileUpload from './FileUpload'
import PipelineWorkflow from '../../components/PipelineWorkflow'
import HeadcountReconciliationModal from '../../components/HeadcountReconciliationModal'
import type { ModalFile } from '../../components/HeadcountReconciliationModal'
import { useApi } from '../../contexts/ApiContext'
import { useDropdownOptions } from '../../utils/useDropdownOptions'
import { analyzeStructuredFiles } from '../../utils/parseStructuredFilesForHeadcount'
import { parseCsvToExcelResult } from '../../utils/parseCsvToExcelResult'

const AVAILABLE_FUNCTIONS = [
  { key: 'HR', label: 'Human Resources', icon: '👥', desc: 'Talent, learning, HR ops, workforce planning' },
  { key: 'Finance', label: 'Finance', icon: '💰', desc: 'FP&A, accounting, treasury, tax, audit' },
  { key: 'Supply Chain', label: 'Supply Chain', icon: '🔗', desc: 'Procurement, logistics, planning, quality' },
  { key: 'Technology', label: 'Technology / IT', icon: '💻', desc: 'Engineering, infra, cyber, data, product' },
  { key: 'Marketing', label: 'Marketing', icon: '📣', desc: 'Brand, digital, content, analytics, campaigns' },
  { key: 'Customer Service', label: 'Customer Service', icon: '🎧', desc: 'Contact center, CX design, case management' },
  { key: 'Legal', label: 'Legal', icon: '⚖️', desc: 'Contracts, compliance, IP, privacy, governance' },
  { key: 'Operations', label: 'Operations', icon: '⚙️', desc: 'Process excellence, facilities, risk, shared services' },
]

export default function InsideOut() {
  const [clientName, setClientName] = useState('')
  const [mmsId, setMmsId] = useState('')
  const [purposeOption, setPurposeOption] = useState('')
  const { industries, purposeOptions } = useDropdownOptions()
  const purpose = purposeOptions.find((o) => o.value === purposeOption)?.label ?? ''
  const [industry, setIndustry] = useState('')
  const [geography, setGeography] = useState('')
  const [primaryColor, setPrimaryColor] = useState('#0047BB')
  const [hexInput, setHexInput] = useState('#0047BB')
  const [accentColor, setAccentColor] = useState<string | undefined>(undefined)
  const [accentInput, setAccentInput] = useState('')
  const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(undefined)
  const [versionLabel, setVersionLabel] = useState('V1')
  const [functionScope, setFunctionScope] = useState<string[]>([])
  const [settings, setSettings] = useState<Settings | null>(null)
  const [projectId, setProjectId] = useState(() => uuidv4())
  const [pipelineReady, setPipelineReady] = useState(false)
  const [showPreFlight, setShowPreFlight] = useState(false)
  const [analyzedFiles, setAnalyzedFiles] = useState<Array<{ base64: string; name: string }>>([])
  const navigate = useNavigate()
  const { refreshProjects } = useOutletContext<{ refreshProjects: () => Promise<void> }>()
  const api = useApi()

  useEffect(() => {
    api.getSettings().then(setSettings)
  }, [api])

  // Keep hex input in sync when color picker changes
  useEffect(() => { setHexInput(primaryColor) }, [primaryColor])

  // ── LLM-assisted brand color lookup (plans/v3-shell-brand-color-palette-plan.md §4.1) ──
  const [lookupStatus, setLookupStatus] = useState<'idle' | 'loading' | 'found' | 'not-found' | 'error'>('idle')
  const [lookupMessage, setLookupMessage] = useState<string | null>(null)
  // Candidates after the top (auto-applied) one — offered as clickable alternates.
  const [otherCandidates, setOtherCandidates] = useState<BrandColorCandidate[]>([])

  function applyCandidate(candidate: BrandColorCandidate) {
    setColorSource('brand')
    if (candidate.primaryColor) { setPrimaryColor(candidate.primaryColor); setHexInput(candidate.primaryColor) }
    // Found a primary but no distinct accent — leave accent unset rather than inventing
    // one, per §4.2: the existing primaryColorDark/Light auto-derive fallback takes over.
    setAccentColor(candidate.accentColor ?? undefined)
    setAccentInput(candidate.accentColor ?? '')
  }

  // Lookup is Claude-only (see brand-lookup.ts) — once settings are known, gate the button
  // instead of letting a non-Claude click silently resolve to the generic "not found" notice.
  const lookupUnsupportedProvider = !!settings && settings.aiProvider !== 'claude'

  async function handleLookupBrandColor() {
    const trimmedName = clientName.trim()
    if (!trimmedName || !settings || settings.aiProvider !== 'claude') return
    setLookupStatus('loading')
    setLookupMessage(null)
    setOtherCandidates([])
    const apiKey =
      settings.aiProvider === 'claude' ? settings.anthropicApiKey
      : settings.aiProvider === 'azure-openai' ? settings.azureApiKey
      : settings.openaiApiKey
    try {
      const result = await api.lookupBrandColors(trimmedName, settings.aiProvider ?? 'claude', apiKey ?? '')
      if (result.verified && result.candidates.length > 0) {
        const [top, ...rest] = result.candidates
        applyCandidate(top)
        setOtherCandidates(rest)
        setLookupStatus('found')
      } else {
        setLookupStatus('not-found')
        setLookupMessage(`No public data about color or theme of ${trimmedName}. Upload a logo or use the color palette below.`)
      }
    } catch (err) {
      setLookupStatus('error')
      setLookupMessage(err instanceof Error ? err.message : 'Could not check brand color right now — please try again.')
    }
  }

  // ── Logo color: an opt-in alternative, never an auto-apply (plan §4.3) ──
  const [logoColorCandidate, setLogoColorCandidate] = useState<string | null>(null)
  const [colorSource, setColorSource] = useState<'brand' | 'logo'>('brand')
  const [savedBrandColor, setSavedBrandColor] = useState<string | null>(null)
  const logoRequestRef = useRef(0)

  useEffect(() => {
    const requestId = ++logoRequestRef.current
    if (!logoDataUrl) {
      setLogoColorCandidate(null)
      // Removing the logo also removes the "Use logo color" option — if that was the
      // active selection, revert the field to the saved brand color rather than leaving
      // it stranded on a now-orphaned logo-derived value with no toggle left to undo it.
      if (colorSource === 'logo') {
        const restored = savedBrandColor || primaryColor
        setHexInput(restored)
        setPrimaryColor(restored)
      }
      setColorSource('brand')
      return
    }
    // A freshly uploaded logo always supersedes whatever candidate the previous one
    // produced — re-extracting (rather than just clearing) is what makes replacing a
    // logo surface "Use logo color" again with the new logo's color.
    extractLogoPalette(logoDataUrl).then((candidate) => {
      if (requestId === logoRequestRef.current) setLogoColorCandidate(candidate)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on a logo change, not on every colorSource/savedBrandColor/primaryColor update
  }, [logoDataUrl])

  const useLogoColor = () => {
    if (!logoColorCandidate) return
    setSavedBrandColor(primaryColor)
    setHexInput(logoColorCandidate)
    setPrimaryColor(logoColorCandidate)
    setColorSource('logo')
  }

  const useBrandColor = () => {
    const restored = savedBrandColor || primaryColor
    setHexInput(restored)
    setPrimaryColor(restored)
    setColorSource('brand')
  }

  const handlePrimaryColorChange = (color: string) => {
    setColorSource('brand')
    setPrimaryColor(color)
  }

  const variants = deriveBrandingVariants(primaryColor)
  const branding: ClientBranding = {
    clientName,
    primaryColor,
    primaryColorDark: variants.primaryColorDark,
    primaryColorLight: variants.primaryColorLight,
    accentColor,
    isWip: true,
    logoDataUrl,
    versionLabel,
  }

  const mmsIdRequired = purposeOption !== '' && (purposeOptions.find((o) => o.value === purposeOption)?.requiresMmsId ?? true)
  const mmsIdError = mmsId.trim() !== '' && !/^\d{8}$/.test(mmsId.trim())
  const mmsIdMissing = mmsIdRequired && mmsId.trim() === ''

  const resetPipelineProject = () => {
    void api.deleteProject(projectId).catch(() => {})
    setProjectId(uuidv4())
    setAnalyzedFiles([])
    setPipelineReady(false)
    setShowPreFlight(false)
    void refreshProjects().catch(() => {})
  }

  const editDocumentsFiles = (updatedFiles: Array<{ base64: string; name: string }>) => {
    void api.deleteProject(projectId).catch(() => {})
    const newId = uuidv4()
    // Re-save under the new ID so finalizeInsideOut and stage handlers can look it up.
    // key={projectId} on PipelineWorkflow forces a remount, clearing all stage state and
    // staleResultRefs so the next run starts clean against the updated file set.
    void api.saveProject({
      id: newId,
      name: formatDashboardTitle(clientName),
      client: clientName,
      industry,
      mode: 'inside-out',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      branding,
      profile: { companyName: clientName, industry, functionScope },
      mmsId: mmsId || undefined,
      purpose: purpose || undefined,
    }).catch(() => {})
    setProjectId(newId)
    setAnalyzedFiles(updatedFiles)
    void refreshProjects().catch(() => {})
  }

  const analyzeStructuredFilesForModal = useCallback(
    (files: ModalFile[]) =>
      analyzeStructuredFiles(files, (b64) => api.parseExcel(b64), parseCsvToExcelResult),
    [api]
  )

  const cancelAnalysis = () => {
    setClientName('')
    setMmsId('')
    setPurposeOption('')
    setIndustry('')
    setGeography('')
    setPrimaryColor('#0047BB')
    setHexInput('#0047BB')
    setAccentColor(undefined)
    setAccentInput('')
    setLogoDataUrl(undefined)
    setVersionLabel('V1')
    setFunctionScope([])
    setLookupStatus('idle')
    setLookupMessage(null)
    setOtherCandidates([])
    setColorSource('brand')
    setSavedBrandColor(null)
    resetPipelineProject()
  }

  const startAnalysis = async (files: Array<{ base64: string; name: string }>) => {
    if (!clientName.trim()) throw new Error('Please enter a client name before uploading documents.')
    if (!purpose.trim()) throw new Error('Please select a purpose before analyzing documents.')
    if (mmsIdRequired && mmsId.trim() === '') throw new Error('MMS ID is required for this purpose.')
    if (mmsIdError) throw new Error('MMS ID must be exactly 8 digits.')

    await api.saveProject({
      id: projectId,
      name: formatDashboardTitle(clientName),
      client: clientName,
      industry,
      mode: 'inside-out',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      branding,
      profile: { companyName: clientName, industry, functionScope },
      mmsId: mmsId || undefined,
      purpose: purpose || undefined,
    })
    void refreshProjects().catch(() => {})
    setAnalyzedFiles(files)
    setShowPreFlight(true)
  }

  const hasApiKey = settings
    ? settings.aiProvider === 'claude'
     ? settings.anthropicApiKey.length > 0
     : settings.aiProvider === 'azure-openai'
        ? settings.azureApiKey.length > 0 &&
          settings.azureEndpoint.length > 0 &&
          settings.azureDeployment.length > 0
        : settings.openaiApiKey.length > 0
   : false

  return (
    <div className="p-8 animate-fade-in w-full max-w-[95rem]">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Inside-Out — Client Data Upload</h1>
      <p className="text-sm text-gray-500 mb-8">
        Upload your client's existing documents or data files. PDFs, Word docs, PowerPoints, Excel/CSV rosters, and Talent Navigator JSON exports are all analyzed to extract workforce data automatically.
      </p>

      {settings && !hasApiKey && (
        <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          ⚠️ No API key configured for {settings.aiProvider}. Document, Excel, CSV, and JSON uploads (PDF/DOCX/PPTX/XLSX/CSV/JSON) all require an API key. Go to <strong>Settings</strong> to add one.
        </div>
      )}

      {/* Client info */}
      <div className="grid grid-cols-2 gap-5 mb-6">
        <div>
          <label htmlFor="io-client-name" className="block text-sm font-semibold text-gray-700 mb-1.5">Client Name</label>
          <input
            id="io-client-name"
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
            placeholder="e.g. Acme Corp"
          />
        </div>
        <div>
          <label htmlFor="io-industry" className="block text-sm font-semibold text-gray-700 mb-1.5">
            Industry
            <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
          </label>
          <select
            id="io-industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
          >
            <option value="">Talent Navigator will infer...</option>
            {industries.map((ind) => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Geography (optional) */}
      <div className="mb-6">
        <label htmlFor="io-geography" className="block text-sm font-semibold text-gray-700 mb-1.5">
          Geography / Market
          <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
        </label>
        <input
          id="io-geography"
          type="text"
          value={geography}
          onChange={(e) => setGeography(e.target.value)}
          className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
          placeholder="e.g. United States, UK, Global"
        />
      </div>

      {/* MMS ID and Purpose */}
      <div className="grid grid-cols-2 gap-5 mb-6">
        <div>
          <label htmlFor="io-mms-id" className="block text-sm font-semibold text-gray-700 mb-1.5">
            MMS ID
            {mmsIdRequired
              ? <span className="ml-1.5 text-xs font-normal text-red-500">(required)</span>
              : <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
            }
          </label>
          <input
            id="io-mms-id"
            type="text"
            value={mmsId}
            onChange={(e) => setMmsId(e.target.value)}
            className={`w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 bg-white ${mmsIdError || mmsIdMissing ? 'border-red-400 focus:ring-red-300' : 'border-gray-200 focus:ring-purple-300'}`}
            placeholder="e.g. 12345678"
          />
          {mmsIdError && <p className="text-xs text-red-500 mt-1">MMS ID must be exactly 8 digits.</p>}
          {mmsIdMissing && !mmsIdError && <p className="text-xs text-red-500 mt-1">MMS ID is required for this purpose.</p>}
        </div>
        <div>
          <label htmlFor="io-purpose" className="block text-sm font-semibold text-gray-700 mb-1.5">Purpose</label>
          <select
            id="io-purpose"
            value={purposeOption}
            onChange={(e) => {
              const selected = purposeOptions.find((o) => o.value === e.target.value)
              setPurposeOption(e.target.value)
              if (selected?.defaultMmsId) {
                setMmsId(selected.defaultMmsId)
              } else if (purposeOptions.find((o) => o.value === purposeOption)?.defaultMmsId) {
                setMmsId('')
              }
            }}
            className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
          >
            <option value="" disabled hidden>Select purpose…</option>
            {purposeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Client Logo — comes first so its extracted color can inform Brand Color below */}
      <LogoUpload value={logoDataUrl} onChange={setLogoDataUrl} />

      {/* Primary Brand Color */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="io-brand-color" className="block text-sm font-semibold text-gray-700">Client Brand Color</label>
          <BrandLookupButton
            onClick={() => void handleLookupBrandColor()}
            disabled={!clientName.trim() || lookupStatus === 'loading' || lookupUnsupportedProvider}
            loading={lookupStatus === 'loading'}
            title={lookupUnsupportedProvider ? 'Brand color lookup requires the Claude provider — change it in Settings.' : undefined}
          />
        </div>

        {/* Mirrors the real v3 dashboard header (solid color, not a gradient) so what's
            previewed here matches what actually ships. */}
        <BrandPreviewHeader
          primaryColor={primaryColor}
          accentColor={accentColor}
          logoUrl={logoDataUrl}
          title={formatDashboardTitle(clientName || 'Client')}
          versionPillText={versionLabel.trim() || undefined}
          showDraftPill
        />

        <div className="flex items-center gap-4 mt-3">
          <input
            type="color"
            aria-label="Client brand color picker"
            value={primaryColor}
            onChange={(e) => handlePrimaryColorChange(e.target.value)}
            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
          />
          <input
            id="io-brand-color"
            type="text"
            value={hexInput}
            onChange={(e) => {
              const val = e.target.value
              setHexInput(val)
              if (/^#[0-9A-Fa-f]{6}$/.test(val)) handlePrimaryColorChange(val)
            }}
            placeholder="#0047BB"
            className="px-4 py-2 text-sm border border-gray-200 rounded-lg font-mono w-32 focus:outline-none focus:ring-2 focus:ring-purple-300"
          />
        </div>
        <p className="text-xs text-gray-400 mt-1">This color will brand the entire navigator — header, tabs, metric cards, and accents.</p>

        {lookupStatus === 'not-found' && lookupMessage && (
          <p role="status" className="text-xs text-gray-500 mt-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            {lookupMessage}
          </p>
        )}
        {lookupStatus === 'error' && lookupMessage && (
          <p role="alert" className="text-xs text-red-500 mt-2">
            {lookupMessage}
          </p>
        )}
        {lookupStatus === 'found' && (
          <p className="text-xs text-purple-600 mt-2">
            Updated from publicly available brand data — review and adjust if needed.
          </p>
        )}

        {/* Other ranked candidates (plan §4.1) — the top one is auto-applied above; these are
            one click away, so a weaker/alternate search match isn't silently discarded. */}
        {lookupStatus === 'found' && otherCandidates.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-gray-500 mb-1.5">Other suggested colors for {clientName}:</p>
            <div className="flex items-center gap-2">
              {otherCandidates.map((candidate, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => applyCandidate(candidate)}
                  title={candidate.primaryColor ?? undefined}
                  aria-label={`Use suggested color ${candidate.primaryColor ?? ''}`}
                  className="w-7 h-7 rounded-md border-2 border-gray-200 hover:border-purple-400 transition-all flex-shrink-0"
                  style={{ background: candidate.primaryColor ?? '#D1D5DB' }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Color source: each option only appears once its source has actually produced a
            color this session — "Use brand color" needs a successful lookup, "Use logo
            color" needs an extractable logo candidate. Neither, one, or both can be true. */}
        {(lookupStatus === 'found' || logoColorCandidate) && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs text-gray-500">Color source:</span>
            {lookupStatus === 'found' && (
              <button
                type="button"
                onClick={useBrandColor}
                className={`px-3 py-1 text-xs font-semibold rounded-full border-2 transition-all ${
                  colorSource === 'brand' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                }`}
              >
                Use brand color
              </button>
            )}
            {logoColorCandidate && (
              <button
                type="button"
                onClick={useLogoColor}
                className={`px-3 py-1 text-xs font-semibold rounded-full border-2 transition-all flex items-center gap-1.5 ${
                  colorSource === 'logo' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                }`}
              >
                <span className="inline-block w-3 h-3 rounded-full border border-gray-200" style={{ background: logoColorCandidate }} />
                Use logo color
              </button>
            )}
          </div>
        )}
      </div>

      {/* Accent / Secondary Color */}
      <div className="mb-6">
        <label htmlFor="io-accent-color" className="block text-sm font-semibold text-gray-700 mb-1.5">
          Accent / Secondary Color
          <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-4">
          <input
            type="color"
            aria-label="Accent color picker"
            value={accentColor || primaryColor}
            onChange={(e) => { setAccentColor(e.target.value); setAccentInput(e.target.value) }}
            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
          />
          <input
            id="io-accent-color"
            type="text"
            value={accentInput}
            onChange={(e) => {
              const val = e.target.value
              setAccentInput(val)
              if (val === '') setAccentColor(undefined)
              else if (/^#[0-9A-Fa-f]{6}$/.test(val)) setAccentColor(val)
            }}
            placeholder="e.g. #0047BB (leave blank to auto-derive)"
            className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-purple-300"
          />
          {accentColor && (
            <button
              type="button"
              onClick={() => { setAccentInput(''); setAccentColor(undefined) }}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Dashboard Version — shown as a pill in the header; defaults to V1, freely editable */}
      <div className="mb-6">
        <label htmlFor="inside-out-version" className="block text-sm font-semibold text-gray-700 mb-1.5">
          Dashboard Version
          <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>
        </label>
        <input
          id="inside-out-version"
          type="text"
          value={versionLabel}
          onChange={(e) => setVersionLabel(e.target.value)}
          placeholder="V1"
          maxLength={60}
          className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
        />
        <p className="text-xs text-gray-400 mt-1">Shown as a pill in the dashboard header. Defaults to V1 and can be edited later.</p>
      </div>

      {/* Function Scope */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Function Scope</label>
        <p className="text-xs text-gray-400 mb-3">Select which business functions the uploaded data covers. The AI will only extract roles from the selected functions.</p>

        {/* Full Enterprise toggle — empty functionScope signals AI to extract ALL roles from document */}
        {(() => {
          const isEnterprise = functionScope.length === 0
          return (
            <button
              type="button"
              onClick={() => setFunctionScope(isEnterprise ? ['HR'] : [])}
              className={`w-full mb-3 p-3.5 rounded-xl border-2 transition-all text-left ${
                isEnterprise
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-dashed border-gray-300 bg-white hover:border-purple-300 hover:bg-purple-50/40'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">🏢</span>
                <div className="flex-1">
                  <div className={`font-bold text-xs ${isEnterprise ? 'text-purple-800' : 'text-gray-800'}`}>
                    Full Enterprise / Entire Organization
                  </div>
                  <div className="text-[10px] text-gray-400">Extract ALL roles from the document — no function restrictions applied</div>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isEnterprise ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                }`}>
                  {isEnterprise && <span className="text-white text-[9px]">✓</span>}
                </div>
              </div>
            </button>
          )
        })()}

        <div className="relative mb-3">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
          <div className="relative flex justify-center text-[10px] text-gray-400 uppercase tracking-widest">
            <span className="bg-white px-3">or select specific functions</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {AVAILABLE_FUNCTIONS.map((fn) => {
            const isSelected = functionScope.includes(fn.key)
            return (
              <button
                key={fn.key}
                type="button"
                onClick={() =>
                  setFunctionScope((prev) => {
                    const next = isSelected ? prev.filter((k) => k !== fn.key) : [...prev, fn.key]
                    return next.length === AVAILABLE_FUNCTIONS.length ? [] : next
                  })
                }
                className={`text-left p-3 rounded-xl border-2 transition-all ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <span className="text-lg">{fn.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold text-xs ${isSelected ? 'text-purple-800' : 'text-gray-800'}`}>
                      {fn.label}
                    </div>
                    <div className="text-[10px] text-gray-400 truncate">{fn.desc}</div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                  }`}>
                    {isSelected && <span className="text-white text-[9px]">✓</span>}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* File upload — keep mounted (hidden) during pre-flight so queued files survive a cancel */}
      {!pipelineReady && (
        <div className={showPreFlight ? 'hidden' : ''}>
          <FileUpload
            onAnalyzeDocuments={startAnalysis}
            clientName={clientName}
            // hasApiKey itself defaults to false while `settings` is still loading
            // (see its definition above) — matching the banner's own `settings &&`
            // guard here too, so the submit button isn't disabled during that
            // brief window for a user who actually has a key configured.
            hasApiKey={!settings || hasApiKey}
          />
        </div>
      )}

      {/* Pre-flight modal — shown after Analyze, before pipeline loads */}
      {showPreFlight && !pipelineReady && (
        <HeadcountReconciliationModal
          initialFiles={analyzedFiles}
          onProceed={(finalFiles) => {
            setAnalyzedFiles(finalFiles)
            setShowPreFlight(false)
            setPipelineReady(true)
          }}
          onCancel={() => {
            // Delete the project saved during startAnalysis so no orphan is left in the list.
            // A fresh projectId ensures the next Analyze attempt starts clean.
            void api.deleteProject(projectId).catch(() => {})
            setProjectId(uuidv4())
            setShowPreFlight(false)
            void refreshProjects().catch(() => {})
          }}
          onAnalyzeStructuredFiles={analyzeStructuredFilesForModal}
        />
      )}

      {/* Pipeline workflow — shown after documents are submitted */}
      {pipelineReady && (
        <div className="mt-4">
          <PipelineWorkflow
            key={projectId}
            projectId={projectId}
            clientName={clientName || 'Client'}
            industry={industry}
            functionScope={functionScope}
            inputFiles={analyzedFiles}
            branding={branding}
            mmsId={mmsId || undefined}
            purpose={purpose || undefined}
            onDone={() => {
              void refreshProjects().catch(() => {})
              navigate(`/dashboard/${projectId}`)
            }}
            onEditDocuments={editDocumentsFiles}
            onUpdateDocuments={resetPipelineProject}
            onCancel={cancelAnalysis}
          />
        </div>
      )}

      {!pipelineReady && (
        <div className="mt-6">
          <button
            onClick={() => navigate('/new')}
            className="py-3 px-6 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all"
          >
            ← Back
          </button>
        </div>
      )}
    </div>
  )
}
